// src/components/CountdownBanner.js
// Shows a live countdown to Sky Bri's next event/appearance
// Sky Bri creates events from the admin panel
import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import './CountdownBanner.css'

function pad(n) { return String(n).padStart(2, '0') }

export default function CountdownBanner() {
  const [announcements, setAnnouncements] = useState([])
  const [current, setCurrent] = useState(0)
  const [timeLeft, setTimeLeft] = useState({})

  useEffect(() => {
    fetchAnnouncements()

    const sub = supabase
      .channel('announcements-live')
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'announcements'
      }, () => fetchAnnouncements())
      .subscribe()

    return () => sub.unsubscribe()
  }, [])

  useEffect(() => {
    if (announcements.length === 0) return
    const interval = setInterval(() => {
      const ann = announcements[current]
      if (!ann?.event_date) return
      const diff = new Date(ann.event_date).getTime() - Date.now()
      if (diff <= 0) { setTimeLeft({ expired: true }); return }
      const days = Math.floor(diff / 86400000)
      const hours = Math.floor((diff % 86400000) / 3600000)
      const mins = Math.floor((diff % 3600000) / 60000)
      const secs = Math.floor((diff % 60000) / 1000)
      setTimeLeft({ days, hours, mins, secs })
    }, 1000)
    return () => clearInterval(interval)
  }, [announcements, current])

  // Rotate banners every 8 seconds
  useEffect(() => {
    if (announcements.length <= 1) return
    const interval = setInterval(() => {
      setCurrent(c => (c + 1) % announcements.length)
    }, 8000)
    return () => clearInterval(interval)
  }, [announcements])

  async function fetchAnnouncements() {
    const { data } = await supabase
      .from('announcements')
      .select('*')
      .eq('active', true)
      .order('event_date', { ascending: true })
    if (data?.length) setAnnouncements(data)
  }

  if (announcements.length === 0) return null

  const ann = announcements[current]
  const hasCountdown = ann.event_date && !timeLeft.expired

  return (
    <div className="countdown-banner">
      <div className="countdown-banner__inner">
        <div className="countdown-banner__content">
          <span className="countdown-banner__eyebrow">Coming Up</span>
          <span className="countdown-banner__title">{ann.title}</span>
          {ann.description && (
            <span className="countdown-banner__desc">{ann.description}</span>
          )}
        </div>

        {hasCountdown && (
          <div className="countdown-banner__timer">
            <div className="countdown-unit">
              <span className="countdown-unit__num">{pad(timeLeft.days || 0)}</span>
              <span className="countdown-unit__label">days</span>
            </div>
            <span className="countdown-sep">:</span>
            <div className="countdown-unit">
              <span className="countdown-unit__num">{pad(timeLeft.hours || 0)}</span>
              <span className="countdown-unit__label">hrs</span>
            </div>
            <span className="countdown-sep">:</span>
            <div className="countdown-unit">
              <span className="countdown-unit__num">{pad(timeLeft.mins || 0)}</span>
              <span className="countdown-unit__label">min</span>
            </div>
            <span className="countdown-sep">:</span>
            <div className="countdown-unit">
              <span className="countdown-unit__num">{pad(timeLeft.secs || 0)}</span>
              <span className="countdown-unit__label">sec</span>
            </div>
          </div>
        )}

        {timeLeft.expired && (
          <div className="countdown-banner__live">
            <span className="countdown-banner__live-dot" />
            HAPPENING NOW
          </div>
        )}
      </div>

      {announcements.length > 1 && (
        <div className="countdown-dots">
          {announcements.map((_, i) => (
            <button
              key={i}
              className={`countdown-dot ${i === current ? 'countdown-dot--active' : ''}`}
              onClick={() => setCurrent(i)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
